import React from 'react';
import * as THREE from 'three';
import type { NPCModelProps, NPCModelRegistry } from '../types';

// ==================== STRANGER THINGS NPCs ====================

// Demodog - quadrupedal creature from the Upside Down
function DemodogModel({ hovered }: NPCModelProps) {
  const dogOpen = hovered;

  return (
    <group>
      {/* Corps principal - masse musclée */}
      <mesh position={[0, 0.22, 0]} rotation={[0.15, 0, 0]} castShadow>
        <capsuleGeometry args={[0.18, 0.4, 12, 20]} />
        <meshStandardMaterial
          color="#1a1a2e"
          roughness={0.85}
          emissive="#2d1a3d"
          emissiveIntensity={hovered ? 0.35 : 0.12}
        />
      </mesh>

      {/* Poitrine bombée */}
      <mesh position={[0, 0.2, 0.15]} castShadow>
        <sphereGeometry args={[0.15, 10, 10]} />
        <meshStandardMaterial color="#1f1f35" roughness={0.9} />
      </mesh>

      {/* Cou épais */}
      <mesh position={[0, 0.28, 0.25]} rotation={[0.4, 0, 0]} castShadow>
        <cylinderGeometry args={[0.1, 0.12, 0.15, 10]} />
        <meshStandardMaterial color="#1a1a2e" roughness={0.9} />
      </mesh>

      {/* Tête - pétales ouverts (bouche fleur) */}
      <group position={[0, 0.32, 0.38]}>
        {/* Base de la tête */}
        <mesh castShadow>
          <sphereGeometry args={[0.14, 12, 12]} />
          <meshStandardMaterial color="#2d1f3d" roughness={0.8} />
        </mesh>

        {/* Pétales de la bouche - 5 grands pétales */}
        {[0, 1, 2, 3, 4].map((i) => {
          const angle = (i * Math.PI * 2) / 5;
          const openAngle = dogOpen ? 1.2 : 0.6;
          return (
            <group key={`petal-group-${i}`}>
              <mesh
                position={[
                  Math.cos(angle) * (dogOpen ? 0.14 : 0.1),
                  0.02,
                  0.08 + Math.sin(angle) * 0.08
                ]}
                rotation={[openAngle, 0, angle]}
                castShadow
              >
                <coneGeometry args={[0.055, 0.14, 5]} />
                <meshStandardMaterial
                  color="#5a1a3a"
                  emissive="#7a2a5a"
                  emissiveIntensity={dogOpen ? 0.35 : 0.15}
                  roughness={0.6}
                />
              </mesh>
              {/* Veines sur les pétales */}
              <mesh
                position={[
                  Math.cos(angle) * (dogOpen ? 0.12 : 0.08),
                  0.01,
                  0.06 + Math.sin(angle) * 0.06
                ]}
                rotation={[openAngle * 0.8, 0, angle]}
              >
                <cylinderGeometry args={[0.005, 0.008, 0.1, 4]} />
                <meshStandardMaterial color="#3a0a2a" emissive="#5a1a3a" emissiveIntensity={0.2} />
              </mesh>
            </group>
          );
        })}

        {/* Intérieur de la bouche - cavité rouge */}
        <mesh position={[0, 0, 0.08]}>
          <sphereGeometry args={[0.08, 10, 10]} />
          <meshStandardMaterial
            color="#4a0a0a"
            emissive="#AA0000"
            emissiveIntensity={dogOpen ? 0.8 : 0.4}
          />
        </mesh>

        {/* Rangées de dents */}
        {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => {
          const toothAngle = (i * Math.PI * 2) / 8;
          return (
            <mesh
              key={`tooth-${i}`}
              position={[Math.cos(toothAngle) * 0.06, 0.02, 0.08 + Math.sin(toothAngle) * 0.04]}
              rotation={[0.3, 0, toothAngle]}
            >
              <coneGeometry args={[0.012, 0.04, 4]} />
              <meshStandardMaterial color="#d4c4b0" roughness={0.4} />
            </mesh>
          );
        })}
      </group>

      {/* Pattes avant - articulées */}
      {[-1, 1].map((side) => (
        <group key={`front-leg-${side}`}>
          {/* Épaule */}
          <mesh position={[side * 0.15, 0.18, 0.1]} castShadow>
            <sphereGeometry args={[0.06, 8, 8]} />
            <meshStandardMaterial color="#1a1a2e" roughness={0.9} />
          </mesh>
          {/* Cuisse */}
          <mesh position={[side * 0.18, 0.1, 0.12]} rotation={[0.3, 0, side * 0.4]} castShadow>
            <capsuleGeometry args={[0.045, 0.12, 6, 10]} />
            <meshStandardMaterial color="#1a1a2e" roughness={0.9} />
          </mesh>
          {/* Tibia */}
          <mesh position={[side * 0.22, 0.02, 0.15]} rotation={[-0.2, 0, side * 0.2]} castShadow>
            <capsuleGeometry args={[0.035, 0.1, 6, 10]} />
            <meshStandardMaterial color="#2d1f3d" roughness={0.9} />
          </mesh>
          {/* Griffes */}
          {[0, 1, 2].map((claw) => (
            <mesh
              key={`front-claw-${side}-${claw}`}
              position={[side * 0.22 + (claw - 1) * 0.02, -0.02, 0.18]}
              rotation={[0.6, 0, 0]}
            >
              <coneGeometry args={[0.01, 0.04, 4]} />
              <meshStandardMaterial color="#2a1a1a" roughness={0.6} />
            </mesh>
          ))}
        </group>
      ))}

      {/* Pattes arrière - plus puissantes */}
      {[-1, 1].map((side) => (
        <group key={`back-leg-${side}`}>
          {/* Hanche */}
          <mesh position={[side * 0.14, 0.15, -0.18]} castShadow>
            <sphereGeometry args={[0.07, 8, 8]} />
            <meshStandardMaterial color="#1a1a2e" roughness={0.9} />
          </mesh>
          {/* Cuisse */}
          <mesh position={[side * 0.16, 0.08, -0.22]} rotation={[-0.4, 0, side * 0.3]} castShadow>
            <capsuleGeometry args={[0.055, 0.14, 6, 10]} />
            <meshStandardMaterial color="#1a1a2e" roughness={0.9} />
          </mesh>
          {/* Tibia */}
          <mesh position={[side * 0.18, 0.02, -0.28]} rotation={[0.3, 0, side * 0.15]} castShadow>
            <capsuleGeometry args={[0.04, 0.1, 6, 10]} />
            <meshStandardMaterial color="#2d1f3d" roughness={0.9} />
          </mesh>
          {/* Griffes */}
          {[0, 1, 2].map((claw) => (
            <mesh
              key={`back-claw-${side}-${claw}`}
              position={[side * 0.18 + (claw - 1) * 0.02, -0.02, -0.32]}
              rotation={[-0.4, 0, 0]}
            >
              <coneGeometry args={[0.012, 0.045, 4]} />
              <meshStandardMaterial color="#2a1a1a" roughness={0.6} />
            </mesh>
          ))}
        </group>
      ))}

      {/* Queue longue et segmentée */}
      {[0, 1, 2, 3].map((seg) => (
        <mesh
          key={`tail-seg-${seg}`}
          position={[0, 0.15 - seg * 0.02, -0.3 - seg * 0.12]}
          rotation={[-0.4 - seg * 0.1, 0, 0]}
          castShadow
        >
          <capsuleGeometry args={[0.04 - seg * 0.008, 0.1, 6, 8]} />
          <meshStandardMaterial color="#2d1f3d" roughness={0.85} />
        </mesh>
      ))}

      {/* Épines dorsales */}
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <mesh
          key={`spine-${i}`}
          position={[0, 0.32 - i * 0.015, -0.15 + i * 0.08]}
          rotation={[-0.5 + i * 0.1, 0, 0]}
          castShadow
        >
          <coneGeometry args={[0.018 - i * 0.002, 0.08 - i * 0.008, 4]} />
          <meshStandardMaterial
            color="#3d2a4d"
            emissive="#4a2a5a"
            emissiveIntensity={0.15}
            roughness={0.7}
          />
        </mesh>
      ))}

      {/* Nervures/veines sur le corps */}
      {[[0.12, 0.2, 0.05], [-0.12, 0.18, -0.05], [0.1, 0.25, -0.1]].map((pos, i) => (
        <mesh key={`vein-${i}`} position={pos as [number, number, number]} rotation={[0, 0, Math.PI / 2 + i * 0.3]}>
          <cylinderGeometry args={[0.008, 0.012, 0.12, 4]} />
          <meshStandardMaterial color="#3d1a4d" emissive="#4a2a5a" emissiveIntensity={0.1} />
        </mesh>
      ))}
    </group>
  );
}

// Demogorgon - tall humanoid creature from the Upside Down
function DemogorgonModel({ hovered }: NPCModelProps) {
  const demoOpen = hovered;

  return (
    <group>
      {/* Torse principal - masse musculaire */}
      <mesh position={[0, 0.55, 0]} castShadow>
        <capsuleGeometry args={[0.22, 0.55, 12, 20]} />
        <meshStandardMaterial
          color="#1a1a2e"
          roughness={0.8}
          emissive="#2d1a3d"
          emissiveIntensity={demoOpen ? 0.45 : 0.18}
        />
      </mesh>

      {/* Poitrine détaillée */}
      <mesh position={[0, 0.5, 0.08]} castShadow>
        <sphereGeometry args={[0.18, 12, 12]} />
        <meshStandardMaterial color="#1f1f35" roughness={0.85} />
      </mesh>

      {/* Côtes visibles */}
      {[0, 1, 2, 3].map((i) => (
        <group key={`ribs-${i}`}>
          <mesh position={[0.12, 0.4 + i * 0.08, 0.05]} rotation={[0, 0, 0.3]}>
            <capsuleGeometry args={[0.015, 0.1, 4, 6]} />
            <meshStandardMaterial color="#2a2a45" roughness={0.9} />
          </mesh>
          <mesh position={[-0.12, 0.4 + i * 0.08, 0.05]} rotation={[0, 0, -0.3]}>
            <capsuleGeometry args={[0.015, 0.1, 4, 6]} />
            <meshStandardMaterial color="#2a2a45" roughness={0.9} />
          </mesh>
        </group>
      ))}

      {/* Cou musclé */}
      <mesh position={[0, 0.92, 0]} castShadow>
        <cylinderGeometry args={[0.12, 0.15, 0.15, 12]} />
        <meshStandardMaterial color="#1a1a2e" roughness={0.85} />
      </mesh>

      {/* Tête - pétales de fleur caractéristiques */}
      <group position={[0, 1.1, 0]}>
        {/* Base de la tête */}
        <mesh castShadow>
          <sphereGeometry args={[0.18, 14, 14]} />
          <meshStandardMaterial color="#2d1f3d" roughness={0.75} />
        </mesh>

        {/* Grands pétales de la bouche (signature du Demogorgon) - 5 pétales */}
        {[0, 1, 2, 3, 4].map((i) => {
          const angle = (i * Math.PI * 2) / 5;
          const openFactor = demoOpen ? 1.6 : 0.8;
          return (
            <group key={`petal-group-${i}`}>
              {/* Pétale principal */}
              <mesh
                position={[
                  Math.cos(angle) * (demoOpen ? 0.22 : 0.15),
                  0.08,
                  Math.sin(angle) * (demoOpen ? 0.22 : 0.15)
                ]}
                rotation={[openFactor, 0, angle]}
                castShadow
              >
                <coneGeometry args={[0.09, 0.22, 6]} />
                <meshStandardMaterial
                  color="#6a1a4a"
                  emissive="#9a2a6a"
                  emissiveIntensity={demoOpen ? 0.5 : 0.25}
                  roughness={0.55}
                />
              </mesh>

              {/* Nervures sur les pétales */}
              {[0, 1, 2].map((v) => (
                <mesh
                  key={`petal-vein-${i}-${v}`}
                  position={[
                    Math.cos(angle) * (demoOpen ? 0.18 : 0.12),
                    0.06 + v * 0.03,
                    Math.sin(angle) * (demoOpen ? 0.18 : 0.12)
                  ]}
                  rotation={[openFactor * 0.9, 0, angle + (v - 1) * 0.15]}
                >
                  <cylinderGeometry args={[0.006, 0.01, 0.15, 4]} />
                  <meshStandardMaterial color="#4a0a3a" emissive="#6a1a4a" emissiveIntensity={0.3} />
                </mesh>
              ))}

              {/* Bord dentelé du pétale */}
              {[0, 1, 2].map((t) => (
                <mesh
                  key={`petal-tooth-${i}-${t}`}
                  position={[
                    Math.cos(angle + (t - 1) * 0.2) * (demoOpen ? 0.28 : 0.2),
                    0.12,
                    Math.sin(angle + (t - 1) * 0.2) * (demoOpen ? 0.28 : 0.2)
                  ]}
                  rotation={[openFactor * 1.1, 0, angle + (t - 1) * 0.2]}
                >
                  <coneGeometry args={[0.02, 0.06, 4]} />
                  <meshStandardMaterial color="#5a1a3a" roughness={0.6} />
                </mesh>
              ))}
            </group>
          );
        })}

        {/* Intérieur de la bouche - gorge rouge sang */}
        <mesh position={[0, 0.1, 0]}>
          <sphereGeometry args={[0.12, 12, 12]} />
          <meshStandardMaterial
            color="#5a0a0a"
            emissive="#CC0000"
            emissiveIntensity={demoOpen ? 1.2 : 0.6}
          />
        </mesh>

        {/* Anneaux de dents intérieures */}
        {[0, 1].map((ring) => (
          <group key={`tooth-ring-${ring}`}>
            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => {
              const toothAngle = (i * Math.PI * 2) / 10;
              const radius = 0.08 + ring * 0.03;
              return (
                <mesh
                  key={`tooth-${ring}-${i}`}
                  position={[
                    Math.cos(toothAngle) * radius,
                    0.1 + ring * 0.02,
                    Math.sin(toothAngle) * radius
                  ]}
                  rotation={[0.4, 0, toothAngle]}
                >
                  <coneGeometry args={[0.012 - ring * 0.003, 0.05 - ring * 0.01, 4]} />
                  <meshStandardMaterial color="#e8d8c8" roughness={0.35} />
                </mesh>
              );
            })}
          </group>
        ))}
      </group>

      {/* Bras longs et musclés */}
      {[-1, 1].map((side) => (
        <group key={`arm-${side}`}>
          {/* Épaule */}
          <mesh position={[side * 0.25, 0.75, 0]} castShadow>
            <sphereGeometry args={[0.08, 10, 10]} />
            <meshStandardMaterial color="#1a1a2e" roughness={0.85} />
          </mesh>

          {/* Bras supérieur */}
          <mesh position={[side * 0.32, 0.6, 0]} rotation={[0.1, 0, side * 0.6]} castShadow>
            <capsuleGeometry args={[0.055, 0.22, 8, 12]} />
            <meshStandardMaterial color="#1a1a2e" roughness={0.85} />
          </mesh>

          {/* Coude */}
          <mesh position={[side * 0.42, 0.42, 0.02]} castShadow>
            <sphereGeometry args={[0.045, 8, 8]} />
            <meshStandardMaterial color="#2d1f3d" roughness={0.9} />
          </mesh>

          {/* Avant-bras */}
          <mesh position={[side * 0.5, 0.28, 0.05]} rotation={[0.4, 0, side * 0.9]} castShadow>
            <capsuleGeometry args={[0.04, 0.22, 8, 12]} />
            <meshStandardMaterial color="#2d1f3d" roughness={0.88} />
          </mesh>

          {/* Main/paume */}
          <mesh position={[side * 0.58, 0.12, 0.08]} castShadow>
            <sphereGeometry args={[0.04, 8, 8]} />
            <meshStandardMaterial color="#1a1a2e" roughness={0.9} />
          </mesh>

          {/* Griffes longues et acérées */}
          {[0, 1, 2, 3, 4].map((claw) => (
            <mesh
              key={`claw-${side}-${claw}`}
              position={[
                side * 0.6 + (claw - 2) * side * 0.02,
                0.08 - claw * 0.01,
                0.1 + claw * 0.015
              ]}
              rotation={[0.6 + claw * 0.05, 0, side * 0.25]}
              castShadow
            >
              <coneGeometry args={[0.012, 0.1 - claw * 0.01, 4]} />
              <meshStandardMaterial color="#2a1a1a" roughness={0.5} metalness={0.2} />
            </mesh>
          ))}
        </group>
      ))}

      {/* Bassin */}
      <mesh position={[0, 0.22, 0]} castShadow>
        <sphereGeometry args={[0.16, 10, 10]} />
        <meshStandardMaterial color="#1a1a2e" roughness={0.9} />
      </mesh>

      {/* Jambes puissantes */}
      {[-1, 1].map((side) => (
        <group key={`leg-${side}`}>
          {/* Cuisse */}
          <mesh position={[side * 0.12, 0.12, 0]} rotation={[0.1, 0, side * 0.15]} castShadow>
            <capsuleGeometry args={[0.07, 0.18, 8, 12]} />
            <meshStandardMaterial color="#1a1a2e" roughness={0.9} />
          </mesh>

          {/* Genou */}
          <mesh position={[side * 0.14, 0, 0.02]} castShadow>
            <sphereGeometry args={[0.05, 8, 8]} />
            <meshStandardMaterial color="#2d1f3d" roughness={0.9} />
          </mesh>

          {/* Tibia */}
          <mesh position={[side * 0.13, -0.12, 0.04]} rotation={[-0.15, 0, side * 0.05]} castShadow>
            <capsuleGeometry args={[0.05, 0.15, 8, 10]} />
            <meshStandardMaterial color="#2d1f3d" roughness={0.9} />
          </mesh>

          {/* Pied griffu */}
          <mesh position={[side * 0.12, -0.22, 0.08]} rotation={[0.5, 0, 0]} castShadow>
            <boxGeometry args={[0.08, 0.04, 0.12]} />
            <meshStandardMaterial color="#1a1a2e" roughness={0.9} />
          </mesh>

          {/* Griffes de pied */}
          {[0, 1, 2].map((toe) => (
            <mesh
              key={`toe-${side}-${toe}`}
              position={[side * 0.12 + (toe - 1) * 0.025, -0.24, 0.14]}
              rotation={[0.8, 0, 0]}
            >
              <coneGeometry args={[0.01, 0.06, 4]} />
              <meshStandardMaterial color="#2a1a1a" roughness={0.6} />
            </mesh>
          ))}
        </group>
      ))}

      {/* Texture de peau - veines pulsantes */}
      {[[0.18, 0.5, 0.08], [-0.18, 0.55, 0.06], [0.15, 0.7, 0.1], [-0.15, 0.65, 0.08], [0.1, 0.35, 0.1]].map((pos, i) => (
        <mesh key={`vein-${i}`} position={pos as [number, number, number]} rotation={[i * 0.2, 0, Math.PI / 2 + i * 0.15]}>
          <cylinderGeometry args={[0.012, 0.018, 0.12 + i * 0.02, 5]} />
          <meshStandardMaterial
            color="#4d1a5d"
            emissive="#6a2a7a"
            emissiveIntensity={demoOpen ? 0.3 : 0.15}
          />
        </mesh>
      ))}

      {/* Épines dorsales */}
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <mesh
          key={`spine-${i}`}
          position={[0, 0.95 - i * 0.12, -0.15 - i * 0.01]}
          rotation={[-0.6, 0, 0]}
          castShadow
        >
          <coneGeometry args={[0.025 - i * 0.003, 0.12 - i * 0.01, 5]} />
          <meshStandardMaterial
            color="#3d2a4d"
            emissive="#5a3a6a"
            emissiveIntensity={0.2}
            roughness={0.65}
          />
        </mesh>
      ))}

      {/* Aura menaçante */}
      {demoOpen && (
        <pointLight position={[0, 0.6, 0]} color="#660033" intensity={2} distance={3} decay={2} />
      )}
    </group>
  );
}

// Mind Flayer - giant tentacled creature dominating the sky
function MindFlayerModel({ hovered }: NPCModelProps) {
  const flayerActive = hovered;

  return (
    <group>
      {/* Corps principal - masse d'ombre */}
      <mesh position={[0, 1.2, 0]} castShadow>
        <sphereGeometry args={[0.5, 16, 16]} />
        <meshStandardMaterial
          color="#0a0a15"
          roughness={0.95}
          emissive="#1a1a3d"
          emissiveIntensity={flayerActive ? 0.5 : 0.2}
          transparent
          opacity={0.9}
        />
      </mesh>

      {/* Tête/cerveau central */}
      <mesh position={[0, 1.6, 0]} castShadow>
        <dodecahedronGeometry args={[0.35, 1]} />
        <meshStandardMaterial
          color="#1a0a2a"
          roughness={0.8}
          emissive="#3a1a5a"
          emissiveIntensity={flayerActive ? 0.6 : 0.25}
        />
      </mesh>

      {/* Yeux multiples luisants */}
      {[[0.15, 1.7, 0.25], [-0.15, 1.7, 0.25], [0, 1.8, 0.2], [0.2, 1.55, 0.3], [-0.2, 1.55, 0.3]].map((pos, i) => (
        <mesh key={`eye-${i}`} position={pos as [number, number, number]}>
          <sphereGeometry args={[0.04 + i * 0.005, 8, 8]} />
          <meshStandardMaterial
            color="#FF0000"
            emissive="#FF0000"
            emissiveIntensity={flayerActive ? 1.5 : 0.8}
          />
        </mesh>
      ))}

      {/* Tentacules principaux (8 tentacules) */}
      {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => {
        const baseAngle = (i * Math.PI * 2) / 8;
        const segments = 6;
        return (
          <group key={`tentacle-${i}`}>
            {Array.from({ length: segments }).map((_, seg) => {
              const segmentAngle = baseAngle + Math.sin(seg * 0.5) * 0.2;
              const radius = 0.4 + seg * 0.25;
              const height = 1.0 - seg * 0.18;
              const thickness = 0.08 - seg * 0.01;
              return (
                <mesh
                  key={`tentacle-seg-${i}-${seg}`}
                  position={[
                    Math.cos(segmentAngle) * radius,
                    height,
                    Math.sin(segmentAngle) * radius
                  ]}
                  rotation={[
                    seg * 0.15,
                    segmentAngle,
                    Math.PI / 2 + seg * 0.2
                  ]}
                  castShadow
                >
                  <capsuleGeometry args={[thickness, 0.2, 6, 10]} />
                  <meshStandardMaterial
                    color="#0a0a18"
                    roughness={0.9}
                    emissive="#1a1a3d"
                    emissiveIntensity={flayerActive ? 0.3 : 0.1}
                  />
                </mesh>
              );
            })}
          </group>
        );
      })}

      {/* Tentacules plus petits autour du corps */}
      {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((i) => {
        const angle = (i * Math.PI * 2) / 12 + 0.15;
        return (
          <mesh
            key={`small-tentacle-${i}`}
            position={[
              Math.cos(angle) * 0.55,
              1.1,
              Math.sin(angle) * 0.55
            ]}
            rotation={[0.5, angle, Math.PI / 2]}
            castShadow
          >
            <coneGeometry args={[0.04, 0.3, 5]} />
            <meshStandardMaterial
              color="#0d0d1a"
              roughness={0.9}
              emissive="#2a1a4a"
              emissiveIntensity={0.15}
            />
          </mesh>
        );
      })}

      {/* Particules de fumée/ombre autour */}
      {[[0.3, 1.4, 0.2], [-0.25, 1.3, -0.3], [0.1, 1.5, -0.25], [-0.2, 1.1, 0.35]].map((pos, i) => (
        <mesh key={`smoke-${i}`} position={pos as [number, number, number]}>
          <sphereGeometry args={[0.1 + i * 0.02, 8, 8]} />
          <meshStandardMaterial
            color="#050510"
            transparent
            opacity={0.4}
            roughness={1}
          />
        </mesh>
      ))}

      {/* Veines d'énergie rouge */}
      {[[0, 1.4, 0.4], [0.35, 1.2, 0.2], [-0.35, 1.25, 0.15], [0.2, 0.9, 0.4], [-0.25, 0.85, 0.35]].map((pos, i) => (
        <mesh key={`vein-${i}`} position={pos as [number, number, number]} rotation={[i * 0.4, i * 0.3, i * 0.5]}>
          <cylinderGeometry args={[0.015, 0.025, 0.25 + i * 0.05, 5]} />
          <meshStandardMaterial
            color="#3a0a1a"
            emissive="#8a1a3a"
            emissiveIntensity={flayerActive ? 0.6 : 0.3}
          />
        </mesh>
      ))}

      {/* Membrane entre tentacules */}
      {[0, 2, 4, 6].map((i) => {
        const angle1 = (i * Math.PI * 2) / 8;
        const angle2 = ((i + 1) * Math.PI * 2) / 8;
        return (
          <mesh
            key={`membrane-${i}`}
            position={[
              (Math.cos(angle1) + Math.cos(angle2)) * 0.3,
              0.9,
              (Math.sin(angle1) + Math.sin(angle2)) * 0.3
            ]}
            rotation={[0, (angle1 + angle2) / 2, 0]}
          >
            <planeGeometry args={[0.4, 0.3]} />
            <meshStandardMaterial
              color="#0a0a15"
              transparent
              opacity={0.5}
              side={THREE.DoubleSide}
              emissive="#1a0a2a"
              emissiveIntensity={0.1}
            />
          </mesh>
        );
      })}

      {/* Éclairs internes pulsants */}
      {flayerActive && (
        <>
          <pointLight position={[0, 1.4, 0]} color="#FF0033" intensity={3} distance={4} decay={2} />
          <pointLight position={[0, 0.8, 0]} color="#3300FF" intensity={1.5} distance={2.5} decay={2} />
        </>
      )}

      {/* Ombre projetée au sol */}
      <mesh position={[0, 0.02, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <circleGeometry args={[0.8, 16]} />
        <meshBasicMaterial color="#000000" transparent opacity={0.4} />
      </mesh>
    </group>
  );
}

// Dustin Henderson - Curly haired kid with baseball cap
function DustinModel({ hovered }: NPCModelProps) {
  return (
    <group>
      {/* Corps - t-shirt */}
      <mesh position={[0, 0.35, 0]} castShadow>
        <cylinderGeometry args={[0.12, 0.15, 0.35, 12]} />
        <meshStandardMaterial
          color="#2d5a4a"
          emissive={hovered ? '#2d5a4a' : '#000000'}
          emissiveIntensity={hovered ? 0.15 : 0}
        />
      </mesh>

      {/* Jean */}
      <mesh position={[0, 0.1, 0]} castShadow>
        <cylinderGeometry args={[0.1, 0.12, 0.2, 10]} />
        <meshStandardMaterial color="#3a5a8a" />
      </mesh>

      {/* Jambes */}
      {[-1, 1].map((side) => (
        <mesh key={`leg-${side}`} position={[side * 0.05, 0, 0]} castShadow>
          <capsuleGeometry args={[0.04, 0.08, 6, 8]} />
          <meshStandardMaterial color="#3a5a8a" />
        </mesh>
      ))}

      {/* Chaussures */}
      {[-1, 1].map((side) => (
        <mesh key={`shoe-${side}`} position={[side * 0.05, -0.06, 0.02]} castShadow>
          <boxGeometry args={[0.06, 0.04, 0.1]} />
          <meshStandardMaterial color="#2a2a2a" />
        </mesh>
      ))}

      {/* Bras */}
      {[-1, 1].map((side) => (
        <group key={`arm-${side}`}>
          <mesh position={[side * 0.16, 0.38, 0]} rotation={[0, 0, side * 0.3]} castShadow>
            <capsuleGeometry args={[0.035, 0.12, 6, 8]} />
            <meshStandardMaterial color="#2d5a4a" />
          </mesh>
          {/* Main */}
          <mesh position={[side * 0.22, 0.28, 0]} castShadow>
            <sphereGeometry args={[0.03, 8, 8]} />
            <meshStandardMaterial color="#ffe4c4" />
          </mesh>
        </group>
      ))}

      {/* Cou */}
      <mesh position={[0, 0.52, 0]} castShadow>
        <cylinderGeometry args={[0.04, 0.05, 0.06, 8]} />
        <meshStandardMaterial color="#ffe4c4" />
      </mesh>

      {/* Tête */}
      <mesh position={[0, 0.62, 0]} castShadow>
        <sphereGeometry args={[0.1, 14, 14]} />
        <meshStandardMaterial color="#ffe4c4" />
      </mesh>

      {/* Cheveux bouclés - la signature de Dustin ! */}
      {/* Base des cheveux */}
      <mesh position={[0, 0.68, 0]} castShadow>
        <sphereGeometry args={[0.11, 12, 12, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <meshStandardMaterial color="#5a3a2a" />
      </mesh>

      {/* Boucles */}
      {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => {
        const angle = (i * Math.PI * 2) / 8;
        return (
          <mesh
            key={`curl-${i}`}
            position={[
              Math.cos(angle) * 0.08,
              0.72,
              Math.sin(angle) * 0.08,
            ]}
            castShadow
          >
            <sphereGeometry args={[0.04, 8, 8]} />
            <meshStandardMaterial color="#5a3a2a" />
          </mesh>
        );
      })}

      {/* Casquette de baseball */}
      <mesh position={[0, 0.74, 0]} castShadow>
        <cylinderGeometry args={[0.11, 0.12, 0.06, 12]} />
        <meshStandardMaterial color="#c41e3a" />
      </mesh>
      {/* Visière de la casquette */}
      <mesh position={[0, 0.72, 0.1]} rotation={[-0.3, 0, 0]} castShadow>
        <boxGeometry args={[0.12, 0.02, 0.08]} />
        <meshStandardMaterial color="#c41e3a" />
      </mesh>

      {/* Yeux */}
      {[-1, 1].map((side) => (
        <group key={`eye-${side}`}>
          <mesh position={[side * 0.035, 0.64, 0.085]} castShadow>
            <sphereGeometry args={[0.02, 8, 8]} />
            <meshStandardMaterial color="#ffffff" />
          </mesh>
          <mesh position={[side * 0.035, 0.64, 0.1]} castShadow>
            <sphereGeometry args={[0.01, 8, 8]} />
            <meshStandardMaterial color="#4a3020" />
          </mesh>
        </group>
      ))}

      {/* Nez */}
      <mesh position={[0, 0.61, 0.09]} castShadow>
        <sphereGeometry args={[0.02, 8, 8]} />
        <meshStandardMaterial color="#ffe4c4" />
      </mesh>

      {/* Sourire (dents iconiques de Dustin !) */}
      <mesh position={[0, 0.57, 0.085]} castShadow>
        <boxGeometry args={[0.04, 0.015, 0.01]} />
        <meshStandardMaterial color="#ffffff" />
      </mesh>

      {/* Taches de rousseur */}
      {[[0.05, 0.59, 0.095], [-0.05, 0.59, 0.095], [0.06, 0.61, 0.09], [-0.06, 0.61, 0.09]].map((pos, i) => (
        <mesh key={`freckle-${i}`} position={pos as [number, number, number]} castShadow>
          <sphereGeometry args={[0.005, 6, 6]} />
          <meshStandardMaterial color="#c4a484" />
        </mesh>
      ))}

      {/* Hover effect */}
      {hovered && (
        <pointLight position={[0, 0.6, 0.3]} intensity={0.5} color="#ffffff" distance={1.5} />
      )}
    </group>
  );
}

// Erica Sinclair - Lucas's sassy little sister with braids
function EricaModel({ hovered }: NPCModelProps) {
  return (
    <group>
      {/* Corps - robe colorée */}
      <mesh position={[0, 0.28, 0]} castShadow>
        <coneGeometry args={[0.12, 0.35, 12]} />
        <meshStandardMaterial
          color="#ff69b4"
          emissive={hovered ? '#ff69b4' : '#000000'}
          emissiveIntensity={hovered ? 0.15 : 0}
        />
      </mesh>

      {/* Ceinture */}
      <mesh position={[0, 0.35, 0]} rotation={[Math.PI / 2, 0, 0]} castShadow>
        <torusGeometry args={[0.08, 0.015, 8, 16]} />
        <meshStandardMaterial color="#ffd700" metalness={0.5} />
      </mesh>

      {/* Jambes */}
      {[-1, 1].map((side) => (
        <mesh key={`leg-${side}`} position={[side * 0.04, 0.05, 0]} castShadow>
          <capsuleGeometry args={[0.03, 0.08, 6, 8]} />
          <meshStandardMaterial color="#8b4513" />
        </mesh>
      ))}

      {/* Chaussures colorées */}
      {[-1, 1].map((side) => (
        <mesh key={`shoe-${side}`} position={[side * 0.04, -0.02, 0.02]} castShadow>
          <boxGeometry args={[0.05, 0.03, 0.08]} />
          <meshStandardMaterial color="#ff1493" />
        </mesh>
      ))}

      {/* Torse */}
      <mesh position={[0, 0.42, 0]} castShadow>
        <cylinderGeometry args={[0.08, 0.1, 0.12, 10]} />
        <meshStandardMaterial color="#ff69b4" />
      </mesh>

      {/* Bras */}
      {[-1, 1].map((side) => (
        <group key={`arm-${side}`}>
          <mesh position={[side * 0.12, 0.42, 0]} rotation={[0, 0, side * 0.5]} castShadow>
            <capsuleGeometry args={[0.025, 0.1, 6, 8]} />
            <meshStandardMaterial color="#8b4513" />
          </mesh>
          {/* Main */}
          <mesh position={[side * 0.18, 0.35, 0]} castShadow>
            <sphereGeometry args={[0.025, 8, 8]} />
            <meshStandardMaterial color="#8b4513" />
          </mesh>
        </group>
      ))}

      {/* Cou */}
      <mesh position={[0, 0.48, 0]} castShadow>
        <cylinderGeometry args={[0.03, 0.035, 0.04, 8]} />
        <meshStandardMaterial color="#8b4513" />
      </mesh>

      {/* Tête */}
      <mesh position={[0, 0.56, 0]} castShadow>
        <sphereGeometry args={[0.085, 14, 14]} />
        <meshStandardMaterial color="#8b4513" />
      </mesh>

      {/* Cheveux - coiffure afro avec tresses */}
      <mesh position={[0, 0.62, 0]} castShadow>
        <sphereGeometry args={[0.09, 12, 12, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <meshStandardMaterial color="#1a0a0a" />
      </mesh>

      {/* Tresses qui descendent */}
      {[-1, 1].map((side) => (
        <group key={`braid-${side}`}>
          {/* Tresse principale */}
          <mesh position={[side * 0.08, 0.52, -0.02]} castShadow>
            <capsuleGeometry args={[0.025, 0.15, 6, 8]} />
            <meshStandardMaterial color="#1a0a0a" />
          </mesh>
          {/* Élastique coloré */}
          <mesh position={[side * 0.08, 0.42, -0.02]} castShadow>
            <sphereGeometry args={[0.03, 8, 8]} />
            <meshStandardMaterial color="#ff1493" />
          </mesh>
        </group>
      ))}

      {/* Yeux grands et expressifs */}
      {[-1, 1].map((side) => (
        <group key={`eye-${side}`}>
          <mesh position={[side * 0.03, 0.58, 0.07]} castShadow>
            <sphereGeometry args={[0.018, 8, 8]} />
            <meshStandardMaterial color="#ffffff" />
          </mesh>
          <mesh position={[side * 0.03, 0.58, 0.085]} castShadow>
            <sphereGeometry args={[0.01, 8, 8]} />
            <meshStandardMaterial color="#2a1a0a" />
          </mesh>
        </group>
      ))}

      {/* Sourcils expressifs (levés = attitude sass) */}
      {[-1, 1].map((side) => (
        <mesh key={`brow-${side}`} position={[side * 0.03, 0.61, 0.075]} rotation={[0, 0, side * -0.2]} castShadow>
          <boxGeometry args={[0.025, 0.008, 0.01]} />
          <meshStandardMaterial color="#1a0a0a" />
        </mesh>
      ))}

      {/* Nez */}
      <mesh position={[0, 0.55, 0.075]} castShadow>
        <sphereGeometry args={[0.015, 8, 8]} />
        <meshStandardMaterial color="#7a5533" />
      </mesh>

      {/* Bouche - petit sourire en coin (sass!) */}
      <mesh position={[0.01, 0.52, 0.07]} rotation={[0, 0, 0.15]} castShadow>
        <torusGeometry args={[0.015, 0.004, 8, 8, Math.PI]} />
        <meshStandardMaterial color="#8b3a3a" />
      </mesh>

      {/* Boucles d'oreilles */}
      {[-1, 1].map((side) => (
        <mesh key={`earring-${side}`} position={[side * 0.085, 0.54, 0]} castShadow>
          <sphereGeometry args={[0.012, 8, 8]} />
          <meshStandardMaterial color="#ffd700" metalness={0.8} roughness={0.2} />
        </mesh>
      ))}

      {/* Hover effect */}
      {hovered && (
        <pointLight position={[0, 0.5, 0.3]} intensity={0.5} color="#ff69b4" distance={1.5} />
      )}
    </group>
  );
}

// Registry export
export const strangerThingsNPCs: NPCModelRegistry = {
  demodog: DemodogModel,
  demogorgon: DemogorgonModel,
  mind_flayer: MindFlayerModel,
  dustin: DustinModel,
  erica: EricaModel,
};
